import { parseImportNumber } from './importer';
import { parseImporte, type ScannedLine } from './receipts';

/**
 * Bloque S (escáner único, 14 oct 2026) · ⚠️ PRECIO UNITARIO, NUNCA EL TOTAL
 * DE LA LÍNEA (Alex: «importantísimo»).
 *
 * Si compras 5 paquetes de 8 panes, el precio del ingrediente es el de UN
 * paquete. Leer el total de la línea como precio deja el ingrediente
 * carísimo y todas sus recetas también. Por eso, de cada línea:
 *
 *  - se lee cantidad · precio unitario · importe;
 *  - SIEMPRE se comprueba cantidad × precio unitario = importe (± 0,05);
 *  - si la factura solo trae el importe, precio unitario = importe ÷ cantidad;
 *  - si no cuadra, falta la cantidad o falta el importe, la línea va a
 *    revisar (ámbar) y su precio NUNCA se aplica solo.
 *
 * Aquí no se decide nada más: solo si el precio unitario es de fiar.
 */

export type DudaPrecio =
  /** cantidad × precio unitario ≠ importe de la línea. */
  | 'no-cuadra'
  /** No se lee cuántos se compraron. */
  | 'sin-cantidad'
  /** No hay importe de la línea con el que comprobarlo. */
  | 'sin-importe'
  /** El precio nuevo es más del triple o menos de un tercio del anterior. */
  | 'x3';

export interface LineaLeida {
  qty?: number;
  unitPrice?: number;
  lineTotal?: number;
  duda?: Exclude<DudaPrecio, 'x3'>;
}

/** Margen de las comprobaciones de dinero del escáner (decidido con Alex). */
export const MARGEN_CUADRE = 0.05;

const r4 = (n: number): number => Math.round(n * 10_000) / 10_000;

/**
 * Las lecturas posibles de la cantidad. «1,000» en una factura española es
 * UNO (con tres decimales) y en una mexicana es MIL: no se adivina, se prueba
 * cuál cuadra con el importe.
 */
function cantidadesPosibles(raw: string | undefined): number[] {
  const texto = String(raw ?? '').replace(/[a-zA-Z]+\.?/g, ' ').trim().split(/\s+/)[0] ?? '';
  if (!texto) return [];
  const out: number[] = [];
  for (const n of [parseImportNumber(texto), parseImporte(texto)]) {
    if (n != null && Number.isFinite(n) && n > 0 && !out.includes(n)) out.push(n);
  }
  return out;
}

export function verificarLinea(line: Pick<ScannedLine, 'quantity' | 'unitPrice' | 'total'>): LineaLeida {
  const total = parseImporte(line.total);
  const leido = parseImporte(line.unitPrice);
  const precioLeido = leido != null && Number.isFinite(leido) && leido > 0 ? leido : undefined;
  const importe = total != null && Number.isFinite(total) && total > 0 ? total : undefined;
  const cantidades = cantidadesPosibles(line.quantity);

  if (!cantidades.length) {
    return { unitPrice: precioLeido, ...(importe != null ? { lineTotal: importe } : {}), duda: 'sin-cantidad' };
  }
  if (importe == null) {
    return { qty: cantidades[0], unitPrice: precioLeido, duda: 'sin-importe' };
  }
  if (precioLeido != null) {
    const qty = cantidades.find((q) => Math.abs(q * precioLeido - importe) <= MARGEN_CUADRE);
    if (qty != null) return { qty, unitPrice: precioLeido, lineTotal: importe };
  }
  const qty = cantidades[0];
  if (precioLeido == null) {
    // «1,000» sin precio unitario: ¿1 o 1000? Nada lo resuelve, así que no se
    // adivina: a revisar, sin proponer un precio.
    if (cantidades.length > 1) return { lineTotal: importe, duda: 'sin-cantidad' };
    // Solo el importe: el unitario sale de dividir, y eso sí es seguro.
    return { qty, unitPrice: r4(importe / qty), lineTotal: importe };
  }
  // No cuadra (descuento por línea, o se leyó el total como unitario): se
  // propone importe ÷ cantidad, pero a revisar.
  return { qty, unitPrice: r4(importe / qty), lineTotal: importe, duda: 'no-cuadra' };
}

/** Freno extra: un precio que se triplica o cae a menos de un tercio casi seguro está mal leído. */
export function fueraDelFreno(anterior: number, nuevo: number): boolean {
  if (!(anterior > 0) || !(nuevo > 0)) return false;
  return nuevo > anterior * 3 || nuevo < anterior / 3;
}
